import React from 'react';
import { format } from 'date-fns';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  MoreHorizontal, Download, Eye, Pencil, Trash2,
  FolderInput, Tag, Eye as EyeIcon, EyeOff, Copy,
  Link2, LayoutGrid, List, FileCheck, PenTool
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { getFileIcon, formatFileSize, isPreviewable } from './fileConfig';

const FileRow = ({ file, isSelected, onSelect, onClick, onAction }) => {
  // Get icon - use FileCheck for signed estimates, otherwise get from config
  const IconComponent = file.is_signed_estimate ? FileCheck : getFileIcon(file.file_type);
  
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 py-3 border-b hover:bg-accent/50 cursor-pointer transition-colors group",
        isSelected && "bg-accent"
      )}
      onClick={() => onClick(file)}
    >
      {/* Checkbox - hide for signed estimates */}
      <div onClick={(e) => e.stopPropagation()}>
        {!file.is_signed_estimate ? (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onSelect(file.id)}
          />
        ) : (
          <div className="w-4" />
        )}
      </div>

      {/* File Icon */}
      <div className={cn(
        "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center",
        file.is_signed_estimate
          ? "bg-green-100 dark:bg-green-900/30"
          : file.file_type?.startsWith('image/') 
            ? "bg-purple-100 dark:bg-purple-900/30" 
            : "bg-blue-100 dark:bg-blue-900/30"
      )}>
        <IconComponent className={cn(
          "h-5 w-5",
          file.is_signed_estimate
            ? "text-green-600 dark:text-green-400"
            : file.file_type?.startsWith('image/') 
              ? "text-purple-600 dark:text-purple-400" 
              : "text-blue-600 dark:text-blue-400"
        )} />
      </div>

      {/* File Name & Meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm truncate">{file.file_name}</p>
          {file.is_signed_estimate && (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs px-1.5 py-0">
              <PenTool className="h-3 w-3 mr-1" />
              Signed
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {file.is_signed_estimate ? (
            <span>Estimate #{file.estimate?.estimate_number}</span>
          ) : (
            <span>{formatFileSize(file.size_bytes)}</span>
          )}
          {file.category_name && (
            <>
              <span>•</span>
              <Badge variant="outline" className="text-xs px-1.5 py-0">
                {file.category_name}
              </Badge>
            </>
          )}
        </div>
      </div>

      {/* Tags */}
      <div className="hidden lg:flex items-center gap-1 max-w-[150px]">
        {file.tags?.slice(0, 2).map((tag, idx) => (
          <Badge key={idx} variant="secondary" className="text-xs px-1.5 py-0">
            {tag}
          </Badge>
        ))}
        {file.tags?.length > 2 && (
          <Badge variant="secondary" className="text-xs px-1.5 py-0">
            +{file.tags.length - 2}
          </Badge>
        )}
      </div>

      {/* Uploader */}
      <div className="hidden md:flex items-center gap-2 w-[120px]">
        <Avatar className="h-6 w-6">
          <AvatarFallback className="text-xs">
            {getInitials(file.uploaded_by_name)}
          </AvatarFallback>
        </Avatar>
        <span className="text-xs text-muted-foreground truncate">
          {file.uploaded_by_name || 'Unknown'}
        </span>
      </div>

      {/* Date */}
      <div className="hidden sm:block w-[100px] text-xs text-muted-foreground">
        {file.uploaded_at ? format(new Date(file.uploaded_at), 'MMM d, yyyy') : ''}
      </div>

      {/* Visibility */}
      <div className="w-[80px]">
        <Badge
          variant={file.visibility === 'customer' ? 'default' : 'secondary'}
          className="text-xs"
        >
          {file.visibility === 'customer' ? (
            <><EyeIcon className="h-3 w-3 mr-1" /> Public</>
          ) : (
            <><EyeOff className="h-3 w-3 mr-1" /> Internal</>
          )}
        </Badge>
      </div>

      {/* Links indicator */}
      {file.linked_entities_count > 0 && (
        <div className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground">
          <Link2 className="h-3 w-3" />
          <span>{file.linked_entities_count}</span>
        </div>
      )}

      {/* Actions */}
      <div onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {file.is_signed_estimate ? (
              // Signed estimate specific menu
              <>
                {file.file_url ? (
                  <>
                    <DropdownMenuItem onClick={() => onAction('preview', file)}>
                      <Eye className="h-4 w-4 mr-2" />
                      View PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onAction('download', file)}>
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem onClick={() => onAction('preview', file)}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Agreement
                  </DropdownMenuItem>
                )}
              </>
            ) : (
              // Regular file menu
              <>
                {isPreviewable(file.file_type) && (
                  <DropdownMenuItem onClick={() => onAction('preview', file)}>
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onAction('download', file)}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onAction('rename', file)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction('category', file)}>
                  <FolderInput className="h-4 w-4 mr-2" />
                  Change Category
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction('tag', file)}>
                  <Tag className="h-4 w-4 mr-2" />
                  Manage Tags
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction('link', file)}>
                  <Link2 className="h-4 w-4 mr-2" />
                  Link to...
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onAction('visibility', file)}>
                  {file.visibility === 'customer' ? (
                    <><EyeOff className="h-4 w-4 mr-2" /> Make Internal</>  
                  ) : (
                    <><EyeIcon className="h-4 w-4 mr-2" /> Make Customer-visible</>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction('version', file)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Upload New Version
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => onAction('delete', file)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export const FileList = ({
  files,
  selectedIds,
  onSelect,
  onFileClick,
  onFileAction,
  viewMode = 'list',
  onViewModeChange
}) => {
  const toggleSelectAll = () => {
    if (selectedIds.length === files.length) {
      // Deselect all
      files.forEach(f => onSelect(f.id));
    } else {
      // Select all
      files.forEach(f => {
        if (!selectedIds.includes(f.id)) {
          onSelect(f.id);
        }
      });
    }
  };

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <div className="text-6xl mb-4">📁</div>
        <p className="text-lg font-medium">No files yet</p>
        <p className="text-sm">Upload your first file to get started</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Table Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/50 text-xs font-medium text-muted-foreground">
        <div className="w-[28px]">
          <Checkbox
            checked={selectedIds.length === files.length && files.length > 0}
            onCheckedChange={toggleSelectAll}
          />
        </div>
        <div className="w-10"></div>
        <div className="flex-1">Name</div>
        <div className="hidden lg:block w-[150px]">Tags</div>
        <div className="hidden md:block w-[120px]">Uploaded by</div>
        <div className="hidden sm:block w-[100px]">Date</div>
        <div className="w-[80px]">Visibility</div>
        <div className="hidden lg:block w-[40px]"></div>
        <div className="w-[40px]"></div>
      </div>

      {/* File Rows */}
      <div className="flex-1 overflow-y-auto">
        {files.map(file => (
          <FileRow
            key={file.id}
            file={file}
            isSelected={selectedIds.includes(file.id)}
            onSelect={onSelect}
            onClick={onFileClick}
            onAction={onFileAction}
          />
        ))}
      </div>
    </div>
  );
};
